import { LockOutlined } from "@ant-design/icons";
import { Form, Input } from "antd";
import { useEffect, useState } from "react";
import islatlong from "validator/lib/isLatLong";
import SchemaFieldTitle from "../SchemaFieldTitle";
import Tooltip from "../Tooltip";

const LatLongInput = ({ value, onChange, precision, format, separator, disabled }) => {
    const [inputValue, setInputValue] = useState(value || "");
    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    const onLatLongValueChange = (event) => {
        const rawInput = event.target.value;
        setInputValue(rawInput);
        onChange(rawInput);
    };

    const onBlur = () => {
        const trimmedInput = inputValue.trim();

        if (!trimmedInput) {
            return;
        }

        const values = trimmedInput.split(separator);
        if (values.length === 2) {
            const [lat, long] = values;
            const parsedLat = parseFloat(lat.trim());
            const parsedLong = parseFloat(long.trim());
            if (!isNaN(parsedLat) && !isNaN(parsedLong)) {
                if (isFinite(parsedLong) && isFinite(parsedLat) && parsedLat >= -90 && parsedLat <= 90 && parsedLong >= -180 && parsedLong <= 180) {
                    const formattedLat = parsedLat.toFixed(precision);
                    const formattedLong = parsedLong.toFixed(precision);
                    const formattedValue = `${formattedLat}${separator}${formattedLong}`;

                    setInputValue(formattedValue);
                    onChange(formattedValue);
                }
            }
        }
    };

    return (
        <Input
            value={inputValue}
            onChange={onLatLongValueChange}
            onBlur={onBlur}
            placeholder={`Enter latitude${separator}longitude (e.g., 40.7128${separator}-74.0060)`}
            disabled={disabled}
        />
    );
};

function FormLatLongField({ fieldErrors, fieldId, name, description, isRequired, precision = 6, format, separator = ",", disabled = false, ...field }) {
    const validateLatLong = (_, value) => {
        if (!value || !value.trim()) {
            return Promise.resolve();
        }
        const trimmedValue = value.trim();
        const values = trimmedValue.split(separator);

        if (values.length !== 2) {
            return Promise.reject(`Please enter both latitude and longitude separated by '${separator}'`);
        }

        const [lat, long] = values.map((v) => v.trim());
        if (!lat || !long) {
            return Promise.reject("Both latitude and longitude values are required");
        }

        const parsedLat = parseFloat(lat);
        const parsedLong = parseFloat(long);
        if (isNaN(parsedLat) || isNaN(parsedLong)) {
            return Promise.reject("Latitude and longitude must be numeric values");
        }

        if (parsedLat < -90 || parsedLat > 90) {
            return Promise.reject("Latitude must be between -90 and 90 degrees");
        }

        if (parsedLong < -180 || parsedLong > 180) {
            return Promise.reject("Longitude must be between -180 and 180 degrees");
        }
        const validatorFormat = `${parsedLat}${separator}${parsedLong}`;
        if (!islatlong(validatorFormat)) {
            return Promise.reject(`Invalid coordinates format`);
        }
        return Promise.resolve();
    };

    return (
        <Form.Item
            key={fieldId}
            name={[fieldId]}
            label={
                <div style={{ display: "flex", alignItems: "center" }}>
                    <SchemaFieldTitle name={name} tags={[]} orientation="horizontal" tagSummary={false} isPrimaryKey={field.isPrimaryKey} />
                    {disabled && (
                        <Tooltip title="Masked data due to PII restriction">
                            <LockOutlined style={{ marginLeft: 8, color: "#1890ff" }} />
                        </Tooltip>
                    )}
                </div>
            }
            tooltip={description}
            rules={[
                {
                    required: isRequired,
                    message: "Latitude and longitude are required",
                },
                {
                    validator: validateLatLong,
                },
            ]}
            {...fieldErrors}
        >
            <LatLongInput precision={precision} format={format} separator={separator} disabled={disabled} />
        </Form.Item>
    );
}

export default FormLatLongField;
